import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface SimpleCustomSelectProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
}

const SimpleCustomSelect: React.FC<SimpleCustomSelectProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; openUp: boolean } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const computePosition = (rect: DOMRect) => {
      const dropdownHeight = Math.min(options.length * 40 + 4, 260);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < dropdownHeight + 16 && spaceAbove > spaceBelow;
      return {
        top: openUp ? rect.top - dropdownHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        openUp
      };
    };

    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        setDropdownPosition(computePosition(buttonRef.current.getBoundingClientRect()));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = Math.min(options.length * 40 + 4, 260);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < dropdownHeight + 16 && spaceAbove > spaceBelow;
      setDropdownPosition({
        top: openUp ? rect.top - dropdownHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        openUp
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div ref={containerRef} className={`relative ${className}`}>
        {name && <input type="hidden" name={name} value={value} />}

        <button
          ref={buttonRef}
          type="button"
          id={id}
          onClick={handleToggle}
          className="w-full min-w-0 px-4 py-2.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] text-sm cursor-pointer transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 focus:shadow-xl focus:shadow-[#1875FF]/20 font-medium text-left flex items-center justify-between group"
        >
          <span className={`truncate ${selectedOption ? 'text-gray-900' : 'text-gray-400'}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-[#1875FF] transition-all duration-300 group-hover:scale-110 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {options.length > 0 ? (
              options.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-2.5 text-left hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium text-sm ${
                    option.value === value ? 'bg-[#1875FF]/10 text-[#1875FF]' : 'text-gray-900'
                  } ${index === 0 ? '' : 'border-t border-gray-100'}`}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">
                No options available
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SimpleCustomSelect;
